import React, { useEffect } from 'react';
import { Paper, Typography, Slide, Button } from '@mui/material';

/**
 * A single notification item that slides up when appearing.
 * It auto-removes after 5 minutes (300,000 ms) or when clicked.
 */
function NotificationItem({ notification, onRemove }) {
  useEffect(() => {
    const timer = setTimeout(() => {
      onRemove(notification.id);
    }, 300000); // Auto-dismiss after 5 minutes
    return () => clearTimeout(timer);
  }, [notification, onRemove]);

  const handleClick = () => {
    onRemove(notification.id);
  };

  return (
    <Slide direction="up" in mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{ padding: 1, marginTop: 1, marginBottom: 1, cursor: 'pointer' }}
        onClick={handleClick}
      >
        <Typography variant="body1">{notification.message}</Typography>
      </Paper>
    </Slide>
  );
}

/**
 * NotificationList displays all notifications in a container.
 * If there are notifications, it shows a "Clear All" button above the list.
 * If there are none, it displays a "No new notifications" message.
 */
export default function NotificationList({ notifications, removeNotification, clearAll }) {
  if (notifications.length === 0) {
    return (
      <div style={{ padding: 8 }}>
        <Typography variant="body2">
          No new notifications
        </Typography>
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column-reverse', // New notifications appear at the bottom
        alignItems: 'flex-end',
      }}
    >
      <Button 
        onClick={clearAll} 
        variant="text" 
        size="small" 
        sx={{ alignSelf: 'stretch', marginBottom: 1 }}
      >
        Clear All
      </Button>
      {notifications.map((notif) => (
        <NotificationItem key={notif.id} notification={notif} onRemove={removeNotification} />
      ))}
    </div>
  );
}
